import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toDataUrlResized } from '../../../utils/common';
import SegmentalMetricCard from './SegmentalMetricCard';
import type { DisplayMode, InbodySegmentalData } from './types';

const BodyDiagram = new URL('@/public/images/inbody-body-diagram-body.png', import.meta.url).href;
const BackgroundDiagram = new URL('@/public/images/inbody-body-diagram-background.png', import.meta.url).href;

interface SegmentalAnalysisBodyProps {
    rowData: InbodySegmentalData | null;
    displayMode: DisplayMode;
    optimizeForPdf?: boolean;
}

const SegmentalAnalysisBody = ({ rowData, displayMode, optimizeForPdf = false }: SegmentalAnalysisBodyProps) => {
    const { t } = useTranslation('physicalResults');
    const [bodyDiagramBase64, setBodyDiagramBase64] = useState<string>('');
    const [backgroundDiagramBase64, setBackgroundDiagramBase64] = useState<string>('');

    useEffect(() => {
        const loadImage = async () => {
            try {
                if (!optimizeForPdf) {
                    setBodyDiagramBase64('');
                    setBackgroundDiagramBase64('');
                    return;
                }

                const [bodyDataUrl, backgroundDataUrl] = await Promise.all([
                    toDataUrlResized(BodyDiagram, {
                        maxWidth: 700,
                        maxHeight: 453,
                        mimeType: 'image/png' as const,
                    }),
                    toDataUrlResized(BackgroundDiagram, {
                        maxWidth: 920,
                        maxHeight: 453,
                        mimeType: 'image/jpeg' as const,
                    }),
                ]);
                setBodyDiagramBase64(bodyDataUrl);
                setBackgroundDiagramBase64(backgroundDataUrl);
            } catch (error) {
                console.error('Failed to load inbody diagrams as base64:', error);
            }
        };
        loadImage();
    }, [optimizeForPdf]);

    return (
        <div className="relative mx-auto h-[453px] w-full overflow-hidden rounded-lg bg-white">
            {/* Background */}
            <img
                src={optimizeForPdf ? backgroundDiagramBase64 || BackgroundDiagram : BackgroundDiagram}
                alt={t('inbodyComposition.viewModal.segmentalAnalysis')}
                className="mx-auto block h-full w-full rounded-lg object-cover"
            />
            {/* Body Diagram Overlay */}
            <div className="absolute inset-0 flex items-center justify-center rounded-lg">
                <div className="relative h-full w-full max-w-[700px] overflow-x-hidden">
                    <img
                        src={optimizeForPdf ? bodyDiagramBase64 || BodyDiagram : BodyDiagram}
                        alt={t('inbodyComposition.viewModal.segmentalAnalysis')}
                        className="block h-full w-full object-cover"
                    />
                    <div className="absolute top-5 right-5 left-5 flex items-start justify-between gap-15">
                        {/* Right Arm Card */}
                        <div className="flex flex-1 flex-col items-end justify-end">
                            <div className="flex w-fit rounded-[10px] border-2 border-white bg-neutral-500/5 backdrop-blur-[7.5px]">
                                <SegmentalMetricCard
                                    valueKg={rowData?.ffmOfRightArmKg}
                                    valuePercentage={rowData?.ffmOfRightArmPercentage}
                                    displayMode={displayMode}
                                    label={t('inbodyComposition.viewModal.ffmOfRightArmKg')}
                                />
                            </div>
                        </div>
                        {/* Left Arm Card */}
                        <div className="flex flex-1 flex-col items-start justify-start">
                            <div className="flex w-fit rounded-[10px] border-2 border-white bg-neutral-500/5 backdrop-blur-[7.5px]">
                                <SegmentalMetricCard
                                    valueKg={rowData?.ffmOfLeftArmKg}
                                    valuePercentage={rowData?.ffmOfLeftArmPercentage}
                                    displayMode={displayMode}
                                    label={t('inbodyComposition.viewModal.ffmOfLeftArmKg')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Trunk Card */}
                    <div className="absolute top-[37.5%] left-1/2 flex -translate-x-1/2 flex-col items-center justify-center rounded-[10px] border-2 border-white bg-neutral-500/5 backdrop-blur-[7.5px]">
                        <SegmentalMetricCard
                            valueKg={rowData?.ffmOfTrunkKg}
                            valuePercentage={rowData?.ffmOfTrunkPercentage}
                            displayMode={displayMode}
                            label={t('inbodyComposition.viewModal.ffmOfTrunkKg')}
                        />
                    </div>
                    <div className="absolute right-5 bottom-5 left-5 flex items-start justify-between gap-15">
                        {/* Right Leg Card */}
                        <div className="flex flex-1 flex-col items-end justify-end">
                            <div className="flex w-fit rounded-[10px] border-2 border-white bg-neutral-500/5 backdrop-blur-[7.5px]">
                                <SegmentalMetricCard
                                    valueKg={rowData?.ffmOfRightLegKg}
                                    valuePercentage={rowData?.ffmOfRightLegPercentage}
                                    displayMode={displayMode}
                                    label={t('inbodyComposition.viewModal.ffmOfRightLegKg')}
                                />
                            </div>
                        </div>
                        {/* Left Leg Card */}
                        <div className="flex flex-1 flex-col items-start justify-start">
                            <div className="flex w-fit rounded-[10px] border-2 border-white bg-neutral-500/5 backdrop-blur-[7.5px]">
                                <SegmentalMetricCard
                                    valueKg={rowData?.ffmOfLeftLegKg}
                                    valuePercentage={rowData?.ffmOfLeftLegPercentage}
                                    displayMode={displayMode}
                                    label={t('inbodyComposition.viewModal.ffmOfLeftLegKg')}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SegmentalAnalysisBody;
